(() => {
/**
 * Removes unavailable variants from the cart without racing mutable line indexes.
 * Availability and line-item keys are rendered by Liquid on each cart row.
 */
class CartAvailabilityChecker {
  constructor() {
    this.isChecking = false;
    this.pendingCheck = false;
    this.checkTimeout = null;
    this.init();
  }

  init() {
    this.setupCartUpdateListener();
    this.setupQuantityChangeListener();
    this.scheduleCheck(0);
  }

  scheduleCheck(delay = 300) {
    clearTimeout(this.checkTimeout);
    this.checkTimeout = setTimeout(() => this.checkAllCartItems(), delay);
  }

  setupCartUpdateListener() {
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'cart-availability-checker') this.scheduleCheck();
      });
    }

    const cartContainer = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (!cartContainer) return;

    this.domObserver = new MutationObserver((mutations) => {
      const cartRowsChanged = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('.cart-item') || node.querySelector?.('.cart-item')),
        ),
      );

      if (cartRowsChanged) this.scheduleCheck();
    });

    this.domObserver.observe(cartContainer, { childList: true, subtree: true });
  }

  setupQuantityChangeListener() {
    document.addEventListener('change', (event) => {
      if (event.target.matches('.quantity__input[data-quantity-variant-id]')) this.scheduleCheck(400);
    });
  }

  async checkAllCartItems() {
    if (this.isChecking) {
      this.pendingCheck = true;
      return;
    }

    this.isChecking = true;

    try {
      const unavailableItems = Array.from(document.querySelectorAll('.cart-item'))
        .filter((cartItem) => cartItem.dataset.variantAvailable === 'false')
        .map((cartItem) => ({
          cartItem,
          key: cartItem.dataset.cartItemKey,
        }))
        .filter((item) => item.key);

      if (unavailableItems.length > 0) await this.removeUnavailableItems(unavailableItems);
    } catch (error) {
      console.error('Error checking cart availability:', error);
    } finally {
      this.isChecking = false;

      if (this.pendingCheck) {
        this.pendingCheck = false;
        this.scheduleCheck(0);
      }
    }
  }

  getSections(cartItem) {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const cartDrawerItems = document.getElementById('CartDrawer-CartItems');
    const cartDrawer = document.querySelector('cart-drawer');
    const isInDrawer = cartItem.closest('cart-drawer-items') || cartItem.closest('#CartDrawer');

    if (isInDrawer && cartDrawerItems) {
      sections.push({ id: 'CartDrawer-CartItems', section: 'cart-drawer', selector: '.js-contents' });

      if (cartDrawer?.querySelector('.cart-drawer__footer')) {
        sections.push({ id: 'cart-drawer-footer', section: 'cart-drawer', selector: '.cart-drawer__footer' });
      }
    } else {
      if (mainCartItems) {
        sections.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
      }

      if (mainCartFooter) {
        sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
      }
    }

    sections.push(
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
    );

    return sections;
  }

  renderSections(state, sections) {
    sections.forEach((sectionData) => {
      const elementToReplace = document.getElementById(sectionData.id);
      const sectionHTML = state.sections?.[sectionData.section];
      if (!elementToReplace || !sectionHTML) return;

      const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newContent = parsed.querySelector(sectionData.selector);
      const oldContent = elementToReplace.matches(sectionData.selector)
        ? elementToReplace
        : elementToReplace.querySelector(sectionData.selector);

      if (oldContent && newContent) oldContent.innerHTML = newContent.innerHTML;
    });
  }

  async removeUnavailableItems(unavailableItems) {
    const updates = Object.fromEntries(unavailableItems.map(({ key }) => [key, 0]));
    const sections = this.getSections(unavailableItems[0].cartItem);
    const response = await fetch(routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates,
        sections: [...new Set(sections.map(({ section }) => section))],
        sections_url: window.location.pathname,
      }),
    });

    if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

    const state = await response.json();
    this.renderSections(state, sections);

    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-availability-checker',
        cartData: state,
      });
    }
  }
}

function initCartAvailabilityChecker() {
  if (!window.cartAvailabilityChecker) {
    window.cartAvailabilityChecker = new CartAvailabilityChecker();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartAvailabilityChecker, { once: true });
} else {
  initCartAvailabilityChecker();
}
})();
